import {
  ChangeDetectorRef,
  Compiler,
  Component,
  ComponentRef,
  Injector,
  Input,
  NgModuleRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Page } from '@bloomreach/spa-sdk';
import { Component as BrComponent } from '@bloomreach/spa-sdk/';
import { NavigationService } from '@frk/eds-components';
import { Logger } from '@utils/logger';
import { InteractiveContentComponentConfig } from '../interactive-content-component.config';
import {
  InteractiveContentDisplayComponent,
  InteractiveContentLazyModule,
} from '../interactive-content-display-component.interface';

const logger = Logger.getLogger('InteractiveContentEntryComponent');

@Component({
  selector: 'ft-interactive-content-entry',
  templateUrl: './interactive-content-entry.component.html',
  styleUrls: ['./interactive-content-entry.component.scss'],
})
export class InteractiveContentEntryComponent implements OnInit, OnDestroy {
  @Input() component!: BrComponent;
  @Input() page!: Page;
  @ViewChild('dynamicCmp', { read: ViewContainerRef, static: false }) container;
  componentRef: ComponentRef<InteractiveContentDisplayComponent>;
  componentType: string;
  componentConfig: InteractiveContentComponentConfig;
  isLoading = true;
  errorMsg: string;
  isContainerWide: boolean; // Check if container wide class needs to be added
  marginBottom: string;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private compiler: Compiler,
    private injector: Injector
  ) {}

  ngOnInit() {
    const brConfig = this.component?.getModels();

    this.componentType = brConfig?.config?.componentType;
    this.isContainerWide = brConfig?.config?.hasContainerWideClass === 'yes';

    this.componentConfig = InteractiveContentComponentConfig.getConfig(
      this.componentType
    );
    this.changeDetector.detectChanges(); // at this point show component if componentType exists

    if (this.componentConfig) {
      if (this.componentConfig.loaded && this.componentConfig.moduleRef) {
        // already loaded
        this.createComponent(this.componentConfig.moduleRef);
      } else {
        // need to load first
        if (!this.componentConfig.multiple) {
          this.componentConfig.loaded = true;
        }
        this.componentConfig
          .loader()
          .then((moduleObj) => moduleObj[Object.keys(moduleObj)[0]])
          .then((module) => this.compiler.compileModuleAsync(module))
          .then((moduleFactory) => moduleFactory.create(this.injector))
          .then((moduleRef) => {
            this.componentConfig.moduleRef = moduleRef;
            this.createComponent(this.componentConfig.moduleRef);
          });
      }
    } else {
      logger.error('No component found: ', this.componentType);
      this.errorMsg = `No component found: ${this.componentType}`;
    }
    this.marginBottom =
      (this.component?.getParameters()?.marginBottom as string) || 'auto';
  }

  /**
   * Interactive component create
   * @param moduleRef - NgModuleRef
   */
  private createComponent(moduleRef: NgModuleRef<unknown>): void {
    if (moduleRef) {
      const componentFactory = (moduleRef.instance as InteractiveContentLazyModule).resolveComponent();
      this.container.clear();
      // UDS-355 this seems to fix NavigationService sometimes having an empty Router
      // WARNING: this fix was found by trial and error - don't know how or why it works
      const customInjector: Injector = Injector.create({
        providers: [{ provide: NavigationService }],
        // WDE-1504 using this.injector instead of moduleRef.injector
        // It fixes injection of ActivatedRoute, though not Router
        parent: this.injector,
      });
      this.componentRef = this.container.createComponent(
        componentFactory,
        null,
        customInjector
      );
      this.errorMsg = null;
      this.componentRef.instance.populate(
        this.getConfig(),
        this.component.getModels()?.labels,
        this.component,
        this.page
      );
      this.isLoading = false;
      this.changeDetector.detectChanges(); // to remove loading message
    } else {
      logger.error(`failed to load module for ${this.componentType}`);
    }
  }

  /**
   * Get Config for interactive component
   */
  private getConfig(): any {
    let brConfig = this.component.getModels()?.document;
    const additionalConfig = this.component.getModels()?.additionalConfig;
    if (brConfig) {
      brConfig = this.page.getContent(brConfig)?.getData()?.json;
      if (brConfig) {
        try {
          brConfig = JSON.parse(brConfig);
        } catch (e) {
          brConfig = { error: e, errorSrc: brConfig };
        }
        if (additionalConfig) {
          brConfig.additionalConfig = this.page
            .getContent(additionalConfig)
            ?.getData();
        }
      }
    }
    const additionalContent = this.component.getModels()?.additionalContent;

    if (!brConfig && additionalContent) {
      brConfig = this.page.getContent(additionalContent)?.getData();
    }

    return brConfig;
  }

  ngOnDestroy() {
    // clean up dynamic component to avoid memory leaks
    this.componentRef?.destroy();
  }
}
